import React, { useState, useEffect } from 'react';
import {
  Brain,
  Mail,
  Globe,
  ArrowRight,
  Github,
  Twitter,
  Linkedin,
  Youtube,
  MapPin,
  Phone,
  Send,
  Sparkles,
  Zap,
  Shield,
  Code,
  Users,
  BookOpen,
  HelpCircle,
  ExternalLink,
  ChevronUp,
  Heart,
  Star,
  Award,
  Rocket,
  MessageCircle
} from 'lucide-react';

const StateOfArtFooter: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    const updateTime = () => {
      setCurrentTime(new Date().toLocaleTimeString('en-US', {
        timeZone: 'America/New_York',
        hour12: false
      }));
    };

    window.addEventListener('scroll', handleScroll);
    updateTime();
    const timeInterval = setInterval(updateTime, 1000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(timeInterval);
    };
  }, []);

  const handleSubscribe = () => {
    if (email) {
      setIsSubscribed(true);
      setEmail('');
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] overflow-hidden">
      {/* Advanced Background Effects */}
      <div className="absolute inset-0">
        {/* Floating Orbs */}
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl animate-pulse delay-500" />

        {/* Animated Particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full opacity-20 animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      {/* Circuit Grid Pattern */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `
            linear-gradient(rgba(139, 92, 246, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(139, 92, 246, 0.4) 1px, transparent 1px),
            radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.2) 2px, transparent 2px),
            radial-gradient(circle at 75% 75%, rgba(139, 92, 246, 0.2) 2px, transparent 2px)
          `,
          backgroundSize: '60px 60px, 60px 60px, 100px 100px, 100px 100px'
        }}
      />

      <div className="relative z-10">
        {/* Newsletter CTA Section */}
        <div className="border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-12">

            <div className="flex justify-center">
              <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-2xl rounded-2xl p-2 border border-white/20 max-w-md w-full">
                <div className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3 bg-transparent text-white placeholder-gray-400 focus:outline-none"
                  />
                  <button
                    onClick={handleSubscribe}
                    disabled={isSubscribed}
                    className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${isSubscribed
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white hover:scale-105 shadow-lg hover:shadow-purple-500/25'
                      }`}
                  >
                    {isSubscribed ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white rounded-full border-t-transparent animate-spin" />
                        <span>Done!</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Send className="w-4 h-4" />
                        <span>Subscribe</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid lg:grid-cols-5 gap-12 mb-16">
            {/* Enhanced Brand Section */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-4 mb-6">
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 rounded-3xl flex items-center justify-center">
                    <Brain className="w-8 h-8 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  </div>
                </div>
                <div>
                  <h3 className="text-4xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                    Selfic AI
                  </h3>
                  <p className="text-gray-400">Intelligent Agent Platform</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">Trusted by 10K+ companies</span>
                  </div>
                </div>
              </div>

              <p className="text-gray-300 mb-8 leading-relaxed text-lg">
                Built for the future of intelligent workflows. Empower your business with
                enterprise-grade AI agents that automate, optimize, and scale your operations seamlessly.
              </p>

              {/* Live Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    <span className="text-xs text-gray-400">Active Agents</span>
                  </div>
                  <div className="text-2xl font-bold text-white">50 +</div>
                  <div className="text-xs text-green-400">↗ +12% this month</div>
                </div>
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-4 border border-white/10">
                  <div className="flex items-center space-x-2 mb-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    <span className="text-xs text-gray-400">Workflows</span>
                  </div>
                  <div className="text-2xl font-bold text-white">1,00  +</div>
                  <div className="text-xs text-green-400">↗ Running daily</div>
                </div>
              </div>

              {/* Social Links with Enhanced Design */}
              {/* Social Links with Enhanced Design */}
              <div className="flex space-x-3">
                <a
                  href="https://x.com/SelficAi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative p-4 rounded-2xl bg-white/10 border border-white/20 text-gray-400 transition-all duration-300 hover:scale-110 hover:text-white hover:bg-blue-600"
                >
                  <Twitter className="w-5 h-5" />
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Twitter
                  </div>
                </a>
                <a

                  href="https://www.linkedin.com/company/selficai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative p-4 rounded-2xl bg-white/10 border border-white/20 text-gray-400 transition-all duration-300 hover:scale-110 hover:text-white hover:bg-blue-700"
                >
                  <Linkedin className="w-5 h-5" />
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    LinkedIn
                  </div>
                </a>
              </div>
            </div>

            {/* Enhanced Navigation Sections */}
            {[
              {
                title: 'Platform',
                icon: <Brain className="w-4 h-4" />,
                links: [
                  { name: 'Agent Studio', href: '#studio', icon: <Code className="w-3 h-3" /> },
                  { name: 'Workflow Builder', href: '#workflow', icon: <Zap className="w-3 h-3" /> },
                  { name: 'Enterprise Security', href: '#security', icon: <Shield className="w-3 h-3" /> },
                  { name: 'API Documentation', href: '#api', icon: <BookOpen className="w-3 h-3" /> },
                  { name: 'Integrations', href: '#integrations', icon: <ExternalLink className="w-3 h-3" /> }
                ]
              },
              {
                title: 'Solution',
                icon: <Rocket className="w-4 h-4" />,
                links: [
                  { name: 'Customer Support', href: '#support', icon: <MessageCircle className="w-3 h-3" /> },
                  { name: 'Sales Automation', href: '#sales', icon: <ArrowRight className="w-3 h-3" /> },
                  { name: 'Data Analysis', href: '#analytics', icon: <Brain className="w-3 h-3" /> },
                  { name: 'Content Generation', href: '#content', icon: <Sparkles className="w-3 h-3" /> },
                  { name: 'Enterprise', href: '#enterprise', icon: <Award className="w-3 h-3" /> }
                ]
              },
              {
                title: 'Resources',
                icon: <BookOpen className="w-4 h-4" />,
                links: [
                  { name: 'Documentation', href: '#docs', icon: <BookOpen className="w-3 h-3" /> },
                  { name: 'Help Center', href: '#help', icon: <HelpCircle className="w-3 h-3" /> },
                  { name: 'Community', href: '#community', icon: <Users className="w-3 h-3" /> },
                  { name: 'Blog & Updates', href: '#blog', icon: <ExternalLink className="w-3 h-3" /> },
                  { name: 'Contact Support', href: '#contact', icon: <Mail className="w-3 h-3" /> }
                ]
              }
            ].map((section, index) => (
              <div key={section.title}>
                <h4 className="text-white font-bold mb-6 flex items-center text-lg">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-lg border border-purple-500/30">
                      {section.icon}
                    </div>
                    <span>{section.title}</span>
                  </div>
                </h4>
                <ul className="space-y-4">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      <a
                        href={link.href}
                        className="flex items-center space-x-3 text-gray-400 hover:text-white transition-all duration-300 group hover:translate-x-1"
                      >
                        <span className="text-purple-400 group-hover:text-purple-300 transition-colors">
                          {link.icon}
                        </span>
                        <span className="group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-300">
                          {link.name}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Enhanced Contact Section */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h5 className="text-white font-semibold text-lg">Email Us</h5>
                  <p className="text-gray-400 text-sm">Quick response guaranteed</p>
                </div>
              </div>
              <p className="text-gray-300 font-mono">contact@selficai.com</p>
              <p className="text-gray-400 text-sm mt-2">We respond within 2 hours during business days</p>
            </div>

            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h5 className="text-white font-semibold text-lg">Website</h5>
                  <p className="text-gray-400 text-sm">Explore our platform</p>
                </div>
              </div>
              <p className="text-gray-300 font-mono">www.selficai.com</p>
              <p className="text-gray-400 text-sm mt-2">Live demos and documentation available</p>
            </div>

            <div className="bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/20 hover:border-white/30 transition-all duration-300 group">
              <div className="flex items-center space-x-4 mb-4">
                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h5 className="text-white font-semibold text-lg">Global Presence</h5>
                  <p className="text-gray-400 text-sm">Local time: {currentTime} EST</p>
                </div>
              </div>
              <p className="text-gray-300">New York • Riyadh • Singapore</p>
              <p className="text-gray-400 text-sm mt-2">24/7 enterprise support worldwide</p>
            </div>
          </div>
        </div>

        {/* Enhanced Bottom Section */}
        <div className="border-t border-white/10 bg-gradient-to-r from-black/40 to-black/20 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
              <div className="flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-8">
                <div className="flex items-center space-x-2 text-gray-400">
                  <span>&copy; 2025 Selfic AI. All rights reserved.</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <span>Built with</span>
                  <Heart className="w-4 h-4 text-red-500 animate-pulse" />
                  <span>for the future of AI</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-400">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-sm">All systems operational</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-6 text-gray-400 text-sm">
                <a href="#privacy" className="hover:text-white transition-colors hover:underline">Privacy Policy</a>
                <a href="#terms" className="hover:text-white transition-colors hover:underline">Terms of Service</a>
                <a href="#security" className="hover:text-white transition-colors hover:underline">Security</a>
                <a href="#cookies" className="hover:text-white transition-colors hover:underline">Cookie Policy</a>
                <a href="#accessibility" className="hover:text-white transition-colors hover:underline">Accessibility</a>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Scroll to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 p-4 bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 hover:from-purple-700 hover:via-blue-700 hover:to-cyan-700 text-white rounded-2xl shadow-2xl hover:scale-110 transition-all duration-300 z-50 group"
          >
            <ChevronUp className="w-6 h-6 group-hover:-translate-y-1 transition-transform" />
            <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              Back to top
            </div>
          </button>
        )}
      </div>
    </footer>
  );
};

export default StateOfArtFooter;